// src/config/loginRedirect.ts
//
// Where to send someone whose session has ended: the sign-in page, carrying the page they were on
// as `?next=` so SignInForm (which only follows same-site paths) brings them straight back to it.

import { safeInAppPath } from '../utils/safeInAppPath';

export function loginPathWithNext(pathname: string, search = ''): string {
    if (!pathname || pathname.startsWith('/login')) return '/login';
    return `/login?next=${encodeURIComponent(pathname + search)}`;
}

/** Where signing in lands when there is no usable `next`. NOT "/" — that is the public marketplace. */
export const DEFAULT_AFTER_LOGIN = '/dashboard';

// Sign-in/sign-up pages: returning to one after signing in would be a loop, or a dead end.
const AUTH_PAGES = new Set(['/login', '/signin', '/signup', '/freetrial']);

/**
 * The `next` from a sign-in link, if it is safe to follow: a path on THIS site that isn't a sign-in
 * page. Anything else — another origin, a scheme (`javascript:`), a protocol-relative or
 * backslash/control-char trick, even when percent-encoded — gives the default. Without this, the
 * sign-in page is an open redirect that phishes our own users. The site check itself is
 * {@link safeInAppPath}, shared with the notification bell's links.
 */
export function safeNextPath(raw: string | null | undefined): string {
    const path = safeInAppPath(raw);
    if (!path) return DEFAULT_AFTER_LOGIN;

    // Case-insensitive, on the decoded path: "/LOGIN" and "/%6cogin" are the sign-in page too.
    // (safeInAppPath has already proven the pathname decodes.)
    const pathname = decodeURIComponent(new URL(path, window.location.origin).pathname);
    if (AUTH_PAGES.has(pathname.toLowerCase().replace(/\/+$/, '') || '/')) return DEFAULT_AFTER_LOGIN;

    return path;
}
